import React, { useState, useEffect, useRef } from 'react';
import { Checklist, ChecklistItem, ChecklistItemEvidenceDefinition, EvidenceFieldDefinition } from '@/firebase/checklistService';
import { ComplianceTemplate, complianceService } from '@/firebase/complianceService';
import { AssetType, assetService } from '@/firebase/assetService';
import { locationService, Location } from '@/firebase/locationService';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { X, Plus, ChevronRight, ChevronLeft, CheckCircle2, Circle, Search, FileUp, Loader2, Sparkles, ChevronDown, Trash2 } from 'lucide-react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import ComplianceForm from './ComplianceForm';

interface ChecklistQuickSetupWizardProps {
  onComplete: (checklistId: string) => void;
  onCancel: () => void;
  initialData?: {
    name?: string;
    description?: string;
    items?: ChecklistItem[];
    templateId?: string;
  };
}

type WizardStep = 'checklist' | 'template' | 'asset-type';

interface WizardData {
  checklist: {
    name: string;
    description: string;
    items: ChecklistItem[];
  };
  templateId: string | null;
  template: ComplianceTemplate | null;
  assetTypeId: string | null; // Selected existing asset type ID
  assetType: {
    name: string;
    description: string;
  } | null; // For creating new asset type
  isCreatingAssetType: boolean; // Whether creating new asset type
  locationIds: string[];
  isLocationSpecific: boolean;
}

const ChecklistQuickSetupWizard: React.FC<ChecklistQuickSetupWizardProps> = ({ onComplete, onCancel, initialData }) => {
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const { hasFeature } = useSubscription();
  
  // Determine initial step: if templateId is provided, start at template step, otherwise checklist
  const getInitialStep = (): WizardStep => {
    if (initialData?.templateId) {
      return 'template';
    }
    return 'checklist';
  };
  
  const [currentStep, setCurrentStep] = useState<WizardStep>(getInitialStep());
  const [templates, setTemplates] = useState<ComplianceTemplate[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isLoadingAssetTypes, setIsLoadingAssetTypes] = useState(false);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [assetTypeSearchTerm, setAssetTypeSearchTerm] = useState('');
  const [categories, setCategories] = useState<string[]>([]);
  
  // File upload state
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isProcessingImport, setIsProcessingImport] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  
  const MAX_IMPORT_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
  const canUseAIImport = hasFeature('ai_assistance');
  const allowedMimeTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  
  const [wizardData, setWizardData] = useState<WizardData>({
    checklist: {
      name: initialData?.name || '',
      description: initialData?.description || '',
      items: initialData?.items || []
    },
    templateId: initialData?.templateId || null,
    template: null,
    assetTypeId: null,
    assetType: null,
    isCreatingAssetType: false,
    locationIds: [],
    isLocationSpecific: false
  });

  const [newItemText, setNewItemText] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  // State for new evidence definition inputs per item
  const [newEvidenceName, setNewEvidenceName] = useState<Record<number, string>>({});
  const [newEvidenceRequired, setNewEvidenceRequired] = useState<Record<number, boolean>>({});
  const [newEvidenceDescription, setNewEvidenceDescription] = useState<Record<number, string>>({});
  const [newEvidenceWorkflowRules, setNewEvidenceWorkflowRules] = useState<Record<number, string>>({});
  
  // State for field definitions management
  const [expandedEvidenceDefs, setExpandedEvidenceDefs] = useState<Set<string>>(new Set()); // "itemIndex:evidenceIndex"
  const [newFieldData, setNewFieldData] = useState<Record<string, Partial<EvidenceFieldDefinition>>>({});

  useEffect(() => {
    loadTemplates();
    loadLocations();
    loadAssetTypes();
  }, [currentOrganization?.id]);

  const loadTemplates = async () => {
    if (!currentOrganization?.id) return;
    setIsLoadingTemplates(true);
    try {
      const result = await complianceService.getAllTemplates(currentOrganization.id);
      if (result.success && result.data) {
        setTemplates(result.data);
        // Extract categories from templates
        const templateCategories = Array.from(new Set(result.data.map(t => t.category).filter(Boolean)));
        setCategories(templateCategories);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Load template details if templateId is provided in initialData
  useEffect(() => {
    if (initialData?.templateId && templates.length > 0) {
      const template = templates.find(t => t.id === initialData.templateId);
      if (template) {
        setWizardData(prev => ({
          ...prev,
          template: template,
          templateId: template.id || null
        }));
      }
    }
  }, [initialData?.templateId, templates]);

  const loadAssetTypes = async () => {
    if (!currentOrganization?.id) return;
    setIsLoadingAssetTypes(true);
    try {
      const result = await assetService.getAllAssetTypes(currentOrganization.id);
      if (result.success && result.data) {
        setAssetTypes(result.data);
      }
    } catch (error) {
      console.error('Error loading asset types:', error);
    } finally {
      setIsLoadingAssetTypes(false);
    }
  };


  const loadLocations = async () => {
    if (!currentOrganization?.id) return;
    
    // Fetch user's full data from Firestore to get locationIds and role
    let userLocationIds: string[] | undefined;
    let userRole: string | undefined;
    if (user?.id) {
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userRef = doc(db, 'users', user.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          userLocationIds = userData.locationIds;
          userRole = userData.role;
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    }
    
    try {
      const result = await locationService.getAccessibleLocations(
        currentOrganization.id,
        user?.id,
        userRole,
        userLocationIds
      );
      if (result.success && result.data) {
        setLocations(result.data);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const steps: Array<{ id: WizardStep; label: string; description: string }> = [
    { id: 'checklist', label: 'Checklist', description: 'Create your checklist' },
    { id: 'template', label: 'Add policy to review', description: 'Link compliance template' },
    { id: 'asset-type', label: 'Asset Type', description: 'Select or create asset type' }
  ];

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);
  const isLastStep = currentStepIndex === steps.length - 1;
  const isFirstStep = currentStepIndex === 0;

  const addChecklistItem = () => {
    if (!newItemText.trim()) return;
    const newItem: ChecklistItem = {
      text: newItemText.trim(),
      order: wizardData.checklist.items.length + 1
    };
    setWizardData({
      ...wizardData,
      checklist: {
        ...wizardData.checklist,
        items: [...wizardData.checklist.items, newItem]
      }
    });
    setNewItemText('');
  };

  const removeChecklistItem = (index: number) => {
    const updatedItems = wizardData.checklist.items.filter((_, i) => i !== index);
    // Reorder items
    updatedItems.forEach((item, i) => {
      item.order = i + 1;
    });
    setWizardData({
      ...wizardData,
      checklist: {
        ...wizardData.checklist,
        items: updatedItems
      }
    });
    // Remove from expanded set
    const newExpanded = new Set(expandedItems);
    newExpanded.delete(index);
    setExpandedItems(newExpanded);
    // Clean up state for removed item
    delete newEvidenceName[index];
    delete newEvidenceRequired[index];
    delete newEvidenceDescription[index];
    delete newEvidenceWorkflowRules[index];
    
    // Clean up field data for all evidence definitions of this item
    const newFieldDataCopy = { ...newFieldData };
    Object.keys(newFieldDataCopy).forEach(key => {
      if (key.startsWith(`${index}:`)) {
        delete newFieldDataCopy[key];
      }
    });
    setNewFieldData(newFieldDataCopy);
    
    // Clean up expanded evidence defs
    const newExpandedEvidenceDefs = new Set(expandedEvidenceDefs);
    expandedEvidenceDefs.forEach(key => {
      if (key.startsWith(`${index}:`)) {
        newExpandedEvidenceDefs.delete(key);
      }
    });
    setExpandedEvidenceDefs(newExpandedEvidenceDefs);
  };

  const moveChecklistItem = (index: number, direction: 'up' | 'down') => {
    if (wizardData.checklist.items.length === 0) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= wizardData.checklist.items.length) return;

    const updatedItems = [...wizardData.checklist.items];
    [updatedItems[index], updatedItems[newIndex]] = [updatedItems[newIndex], updatedItems[index]];
    
    // Update order
    updatedItems.forEach((item, i) => {
      item.order = i + 1;
    });
    
    setWizardData({
      ...wizardData,
      checklist: {
        ...wizardData.checklist,
        items: updatedItems
      }
    });
  };

  const handleLocationToggle = (locationId: string) => {
    setWizardData(prev => {
      const currentIds = prev.locationIds || [];
      const newIds = currentIds.includes(locationId)
        ? currentIds.filter(id => id !== locationId)
        : [...currentIds, locationId];
      return { ...prev, locationIds: newIds };
    });
  };

  const handleLocationSpecificToggle = (checked: boolean) => {
    setWizardData(prev => ({
      ...prev,
      isLocationSpecific: checked,
      locationIds: checked ? prev.locationIds : []
    }));
  };

  const filteredLocations = locations.filter(location =>
    location.name.toLowerCase().includes(locationSearchTerm.toLowerCase())
  );

  // Evidence Definitions Management
  const toggleItemExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const addEvidenceDefinition = (itemIndex: number) => {
    const evidenceName = newEvidenceName[itemIndex]?.trim();
    if (!evidenceName) return;

    const updatedItems = [...wizardData.checklist.items];
    if (!updatedItems[itemIndex]) return;

    const newEvidenceDef: ChecklistItemEvidenceDefinition = {
      evidenceName,
      required: newEvidenceRequired[itemIndex] ?? false,
      // evidenceType and source will be auto-populated by backend
      description: newEvidenceDescription[itemIndex]?.trim() || undefined,
      workflowRules: newEvidenceWorkflowRules[itemIndex]?.trim() || undefined,
    };

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      evidenceDefinitions: [...(updatedItems[itemIndex].evidenceDefinitions || []), newEvidenceDef]
    };

    setWizardData({
      ...wizardData,
      checklist: {
        ...wizardData.checklist,
        items: updatedItems
      }
    });

    // Clear input fields for this item
    setNewEvidenceName({ ...newEvidenceName, [itemIndex]: '' });
    setNewEvidenceRequired({ ...newEvidenceRequired, [itemIndex]: false });
    setNewEvidenceDescription({ ...newEvidenceDescription, [itemIndex]: '' });
    setNewEvidenceWorkflowRules({ ...newEvidenceWorkflowRules, [itemIndex]: '' });
  };

  const removeEvidenceDefinition = (itemIndex: number, evidenceIndex: number) => {
    const updatedItems = [...wizardData.checklist.items];
    if (!updatedItems[itemIndex] || !updatedItems[itemIndex].evidenceDefinitions) return;

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      evidenceDefinitions: updatedItems[itemIndex].evidenceDefinitions!.filter((_, i) => i !== evidenceIndex)
    };

    setWizardData({
      ...wizardData,
      checklist: {
        ...wizardData.checklist,
        items: updatedItems
      }
    });

    // Clean up expanded state and field data
    const evidenceKey = getEvidenceDefKey(itemIndex, evidenceIndex);
    const newExpanded = new Set(expandedEvidenceDefs);
    newExpanded.delete(evidenceKey);
    setExpandedEvidenceDefs(newExpanded);

    // Remove all field data for this evidence definition
    const newFieldDataCopy = { ...newFieldData };
    Object.keys(newFieldDataCopy).forEach(key => {
      if (key.startsWith(`${itemIndex}:${evidenceIndex}:`)) {
        delete newFieldDataCopy[key];
      }
    });
    setNewFieldData(newFieldDataCopy);
  };

  // Field Definitions Management
  const getEvidenceDefKey = (itemIndex: number, evidenceIndex: number) => `${itemIndex}:${evidenceIndex}`;
  const getFieldKey = (itemIndex: number, evidenceIndex: number, fieldIndex: number | 'new') => `${itemIndex}:${evidenceIndex}:${fieldIndex}`;

  const toggleEvidenceDefExpanded = (itemIndex: number, evidenceIndex: number) => {
    const key = getEvidenceDefKey(itemIndex, evidenceIndex);
    const newExpanded = new Set(expandedEvidenceDefs);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedEvidenceDefs(newExpanded);
  };

  const addFieldDefinition = (itemIndex: number, evidenceIndex: number) => {
    const fieldKey = getFieldKey(itemIndex, evidenceIndex, 'new');
    const fieldData = newFieldData[fieldKey];
    
    if (!fieldData || !fieldData.key || !fieldData.label || !fieldData.type) {
      return; // Validation
    }

    const newField: EvidenceFieldDefinition = {
      key: fieldData.key.trim(),
      label: fieldData.label.trim(),
      type: fieldData.type,
      required: fieldData.required ?? false,
      // Handle fileNameKeys: if multiple keys, store as fileNameKeys array; if single, store as fileNameKey (backward compat)
      fileNameKeys: fieldData.fileNameKeys && fieldData.fileNameKeys.length > 1 
        ? fieldData.fileNameKeys.filter(k => k && k.trim().length > 0).map(k => k.trim())
        : undefined,
      fileNameKey: (() => {
        const keys = fieldData.fileNameKeys && fieldData.fileNameKeys.length > 0
          ? fieldData.fileNameKeys.filter(k => k && k.trim().length > 0).map(k => k.trim())
          : [];
        return keys.length === 1 ? keys[0] : (fieldData.fileNameKey?.trim() || undefined);
      })(),
      enumValues: fieldData.type === 'enum' && fieldData.enumValues ? fieldData.enumValues : undefined,
      workflowRules: fieldData.workflowRules?.trim() || undefined,
      extractionHints: fieldData.extractionHints,
      normalizationRules: fieldData.normalizationRules,
      validationRules: fieldData.validationRules,
    };

    const updatedItems = [...wizardData.checklist.items];
    if (!updatedItems[itemIndex] || !updatedItems[itemIndex].evidenceDefinitions) return;

    const evidenceDefs = [...updatedItems[itemIndex].evidenceDefinitions!];
    evidenceDefs[evidenceIndex] = {
      ...evidenceDefs[evidenceIndex],
      fieldDefinitions: [...(evidenceDefs[evidenceIndex].fieldDefinitions || []), newField]
    };

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      evidenceDefinitions: evidenceDefs
    };

    setWizardData({
      ...wizardData,
      checklist: {
        ...wizardData.checklist,
        items: updatedItems
      }
    });

    // Clear new field data
    const newFieldDataCopy = { ...newFieldData };
    delete newFieldDataCopy[fieldKey];
    setNewFieldData(newFieldDataCopy);
  };

  const removeFieldDefinition = (itemIndex: number, evidenceIndex: number, fieldIndex: number) => {
    const updatedItems = [...wizardData.checklist.items];
    if (!updatedItems[itemIndex] || !updatedItems[itemIndex].evidenceDefinitions) return;

    const evidenceDefs = [...updatedItems[itemIndex].evidenceDefinitions!];
    const fieldDefs = (evidenceDefs[evidenceIndex].fieldDefinitions || []).filter((_, i) => i !== fieldIndex);

    evidenceDefs[evidenceIndex] = {
      ...evidenceDefs[evidenceIndex],
      fieldDefinitions: fieldDefs.length > 0 ? fieldDefs : undefined
    };

    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      evidenceDefinitions: evidenceDefs
    };

    setWizardData({
      ...wizardData,
      checklist: {
        ...wizardData.checklist,
        items: updatedItems
      }
    });
  };

  const validateImportFile = (file: File): string | null => {
    if (file.size > MAX_IMPORT_FILE_SIZE) {
      return 'File is too large. Please upload a document smaller than 20 MB.';
    }

    const fileName = file.name.toLowerCase();
    const isAllowedType =
      allowedMimeTypes.includes(file.type) ||
      file.type.startsWith('image/') ||
      fileName.endsWith('.pdf') ||
      fileName.endsWith('.doc') ||
      fileName.endsWith('.docx') ||
      fileName.endsWith('.png') ||
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg');

    if (!isAllowedType) {
      return 'Unsupported file type. Please upload a PDF, Word document, or image file.';
    }

    return null;
  };

  const handleImportFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateImportFile(file);
    if (validationError) {
      setImportError(validationError);
      setImportFile(null);
      if (uploadInputRef.current) {
        uploadInputRef.current.value = '';
      }
      return;
    }

    setImportFile(file);
    setImportError(null);
  };

  const handleImportDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(true);
  };

  const handleImportDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
  };

  const handleImportDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);

    const file = event.dataTransfer.files?.[0];
    if (!file) return;

    const validationError = validateImportFile(file);
    if (validationError) {
      setImportError(validationError);
      setImportFile(null);
      return;
    }

    setImportFile(file);
    setImportError(null);
  };

  const handleProcessImport = async () => {
    if (!importFile) {
      setImportError('Please choose a checklist file to upload.');
      return;
    }

    if (!canUseAIImport) {
      setImportError('Upgrade to Standard or Pro to use AI checklist import.');
      return;
    }

    setIsProcessingImport(true);
    setImportError(null);

    try {
      const { extractChecklistFromDocument, isGeminiConfigured } = await import('@/services/geminiService');

      if (!isGeminiConfigured()) {
        throw new Error('Gemini AI is not configured. Please contact support.');
      }

      const extraction = await extractChecklistFromDocument(importFile);
      const rawItems = extraction.items || [];

      const normalizedItems = rawItems.map((item, index) => {
        const baseText = item.text?.trim() || `Step ${index + 1}`;
        const noteText = item.notes?.trim();
        return {
          order: item.order || index + 1,
          text: baseText, // Use clean text (already cleaned in extraction)
          evidenceDefinitions: item.evidenceDefinitions || undefined, // Preserve evidence definitions
        };
      });

      if (normalizedItems.length === 0) {
        throw new Error('No checklist steps were identified in that document. Please upload a clearer checklist.');
      }

      // Update wizard data with extracted information
      const descriptionWithNotes = extraction.description 
        ? (extraction.confidenceNotes 
          ? `${extraction.description}\n\nAI notes: ${extraction.confidenceNotes}`
          : extraction.description)
        : (extraction.confidenceNotes 
          ? `AI notes: ${extraction.confidenceNotes}`
          : undefined);

      setWizardData(prev => ({
        ...prev,
        checklist: {
          name: extraction.name || prev.checklist.name,
          description: descriptionWithNotes || prev.checklist.description,
          items: normalizedItems
        }
      }));

      // Clear file input
      if (uploadInputRef.current) {
        uploadInputRef.current.value = '';
      }
      setImportFile(null);
      showNotification('Checklist items extracted successfully! Review and edit as needed.', 'success');
    } catch (error: any) {
      console.error('Checklist import failed:', error);
      const message = typeof error?.message === 'string'
        ? error.message
        : 'Failed to analyze the document. Please try again with a clearer file.';
      setImportError(message);
    } finally {
      setIsProcessingImport(false);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    setWizardData({
      ...wizardData,
      templateId,
      template: template || null
    });
  };

  const handleTemplateCreated = async (templateData: Partial<ComplianceTemplate>) => {
    if (!currentOrganization?.id) {
      showNotification('Organization is required', 'error');
      return;
    }

    try {
      const userInfo = user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined;
      const result = await complianceService.addTemplate(
        templateData as Omit<ComplianceTemplate, 'id'>,
        currentOrganization.id,
        userInfo
      );

      if (result.success && result.id) {
        // Fetch the created template
        const getResult = await complianceService.getTemplate(result.id);
        if (getResult.success && getResult.data) {
          const newTemplate = getResult.data;
          setTemplates([...templates, newTemplate]);
          
          // Update categories
          if (newTemplate.category && !categories.includes(newTemplate.category)) {
            setCategories([...categories, newTemplate.category]);
          }
          
          setWizardData({
            ...wizardData,
            templateId: newTemplate.id || null,
            template: newTemplate
          });
          setIsCreatingTemplate(false);
          showNotification('Compliance template created successfully', 'success');
        } else {
          showNotification('Template created but could not load details', 'info');
          setIsCreatingTemplate(false);
        }
      } else {
        showNotification(`Error creating template: ${result.error || 'Unknown error'}`, 'error');
      }
    } catch (error: any) {
      console.error('Error creating template:', error);
      showNotification(`Error: ${error.message || 'Failed to create template'}`, 'error');
    }
  };

  const handleNext = () => {
    // Validate current step
    if (currentStep === 'checklist') {
      if (!wizardData.checklist.name.trim()) {
        showNotification('Please enter a checklist name', 'error');
        return;
      }
      if (wizardData.checklist.items.length === 0) {
        showNotification('Please add at least one checklist item', 'error');
        return;
      }
      if (wizardData.isLocationSpecific && wizardData.locationIds.length === 0) {
        showNotification('Please select at least one location or disable location restriction', 'error');
        return;
      }
    } else if (currentStep === 'template') {
      if (!wizardData.templateId) {
        showNotification('Please select or create a compliance template', 'error');
        return;
      }
    } else if (currentStep === 'asset-type') {
      if (!wizardData.assetTypeId && !wizardData.isCreatingAssetType) {
        showNotification('Please select or create an asset type', 'error');
        return;
      }
      if (wizardData.isCreatingAssetType && (!wizardData.assetType?.name || !wizardData.assetType.name.trim())) {
        showNotification('Please enter an asset type name', 'error');
        return;
      }
    }

    // Move to next step
    if (isLastStep) {
      handleComplete();
    } else {
      const nextStepIndex = currentStepIndex + 1;
      setCurrentStep(steps[nextStepIndex].id);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      const prevStepIndex = currentStepIndex - 1;
      setCurrentStep(steps[prevStepIndex].id);
    }
  };

  const handleComplete = async () => {
    if (!currentOrganization?.id) {
      showNotification('Organization is required', 'error');
      return;
    }

    if (!wizardData.checklist.name.trim() || !wizardData.templateId || (!wizardData.assetTypeId && !wizardData.isCreatingAssetType)) {
      showNotification('Please complete all required fields', 'error');
      return;
    }

    setIsSaving(true);
    try {
      // Step 1: Create or get template (already done if selected)
      const templateId = wizardData.templateId!;

      // Step 2: Create asset type if creating new, otherwise use selected one
      let assetTypeId: string;
      if (wizardData.isCreatingAssetType && wizardData.assetType) {
        const assetTypeData: Omit<AssetType, 'id'> = {
          name: wizardData.assetType.name,
          description: wizardData.assetType.description,
          complianceTemplateIds: [templateId]
        };
        const assetTypeResult = await assetService.addAssetType(
          assetTypeData,
          currentOrganization.id
        );
        if (assetTypeResult.success && assetTypeResult.id) {
          assetTypeId = assetTypeResult.id;
          showNotification('Asset type created successfully', 'success');
        } else {
          showNotification(`Error creating asset type: ${assetTypeResult.error || 'Unknown error'}`, 'error');
          return;
        }
      } else if (wizardData.assetTypeId) {
        assetTypeId = wizardData.assetTypeId;
        
        // Update existing asset type to include this template in complianceTemplateIds
        // This ensures the checklist will be found when querying by asset type
        const existingAssetTypeResult = await assetService.getAssetType(assetTypeId);
        if (existingAssetTypeResult.success && existingAssetTypeResult.data) {
          const existingAssetType = existingAssetTypeResult.data;
          const currentTemplateIds = existingAssetType.complianceTemplateIds || [];
          
          // Add templateId if not already in the array
          if (!currentTemplateIds.includes(templateId)) {
            const updatedTemplateIds = [...currentTemplateIds, templateId];
            const updateResult = await assetService.updateAssetType(assetTypeId, {
              complianceTemplateIds: updatedTemplateIds
            });
            
            if (!updateResult.success) {
              console.warn(`Failed to update asset type with template: ${updateResult.error}`);
              // Continue anyway - checklist will still be created
            }
          }
        }
      } else {
        showNotification('Asset type is required', 'error');
        return;
      }

      // Step 3: Create checklist (without assetTypeIds - using template-based relationship instead)
      const checklistData: Partial<Checklist> = {
        name: wizardData.checklist.name,
        description: wizardData.checklist.description || undefined,
        items: wizardData.checklist.items,
        templateId: templateId,
        variables: [],
        locationIds: wizardData.isLocationSpecific && wizardData.locationIds.length > 0 
          ? wizardData.locationIds 
          : undefined
      };

      const { checklistService } = await import('@/firebase/checklistService');
      // Clean up legacy categoryCode fields and ensure evidenceName is used
      if (checklistData.items) {
        checklistData.items = checklistData.items.map(item => {
          if (item.evidenceDefinitions) {
            return {
              ...item,
              evidenceDefinitions: item.evidenceDefinitions.map(evDef => {
                const cleaned: ChecklistItemEvidenceDefinition = {
                  evidenceName: evDef.evidenceName || (evDef as any).categoryCode || 'Unknown Evidence',
                  required: evDef.required,
                  description: evDef.description,
                  evidenceType: evDef.evidenceType,
                  source: evDef.source,
                  fieldDefinitions: evDef.fieldDefinitions
                };
                // Explicitly remove categoryCode if it exists (legacy field)
                if ((evDef as any).categoryCode) {
                  delete (cleaned as any).categoryCode;
                }
                return cleaned;
              })
            };
          }
          return item;
        });
      }
      
      const checklistResult = await checklistService.addChecklist(
        checklistData as Omit<Checklist, 'id'>,
        currentOrganization.id,
        user ? { id: user.id, name: user.name || 'Unknown', email: user.email || 'Unknown' } : undefined
      );

      if (checklistResult.success && checklistResult.id) {
        showNotification('Checklist created successfully!', 'success');
        onComplete(checklistResult.id);
      } else {
        showNotification(`Error creating checklist: ${checklistResult.error || 'Unknown error'}`, 'error');
      }
    } catch (error: any) {
      console.error('Error completing wizard:', error);
      showNotification(`Error: ${error.message || 'Failed to create checklist'}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const renderProgressIndicator = () => {
    return (
      <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-200">
        {steps.map((step, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = index === currentStepIndex;
          const stepNum = index + 1;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center flex-1">
                <div className="flex items-center gap-2 mb-1">
                  {isCompleted ? (
                    <CheckCircle2 className="w-6 h-6 text-primary-600" />
                  ) : isCurrent ? (
                    <Circle className="w-6 h-6 text-primary-600 fill-primary-600" />
                  ) : (
                    <Circle className="w-6 h-6 text-gray-400" />
                  )}
                  <span className={`text-sm font-medium ${isCurrent ? 'text-primary-600' : isCompleted ? 'text-gray-700' : 'text-gray-400'}`}>
                    Step {stepNum}
                  </span>
                </div>
                <span className={`text-xs ${isCurrent ? 'text-gray-700' : 'text-gray-500'}`}>
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${isCompleted ? 'bg-primary-600' : 'bg-gray-300'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderChecklistStep = () => {
    return (
      <div className="space-y-6 p-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Your Checklist</h3>
          <p className="text-sm text-gray-600">Start by creating your checklist with items that need to be checked.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Checklist Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={wizardData.checklist.name}
            onChange={(e) => setWizardData({
              ...wizardData,
              checklist: { ...wizardData.checklist, name: e.target.value }
            })}
            placeholder="e.g., Fire Extinguisher Inspection"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description (Optional)
          </label>
          <textarea
            value={wizardData.checklist.description}
            onChange={(e) => setWizardData({
              ...wizardData,
              checklist: { ...wizardData.checklist, description: e.target.value }
            })}
            placeholder="Brief description of this checklist..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Checklist Items <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => uploadInputRef.current?.click()}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              disabled={isProcessingImport}
            >
              <FileUp className="w-4 h-4" />
              <Sparkles className="w-4 h-4 text-orange-500" />
              {isProcessingImport ? 'Processing...' : 'Upload & Extract (AI)'}
            </button>
          </div>

          {/* File Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-4 mb-3 text-center cursor-pointer transition-colors ${
              isDragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-primary-500'
            }`}
            onClick={() => uploadInputRef.current?.click()}
            onDragOver={handleImportDragOver}
            onDragEnter={handleImportDragOver}
            onDragLeave={handleImportDragLeave}
            onDrop={handleImportDrop}
          >
            <input
              ref={uploadInputRef}
              type="file"
              accept=".pdf,.doc,.docx,image/*"
              className="hidden"
              onChange={handleImportFileChange}
            />
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2">
                <FileUp className="w-8 h-8 text-primary-500" />
                <Sparkles className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  {importFile ? importFile.name : 'Click to upload or drag checklist here'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Supported: PDF, Word (.doc, .docx), PNG, JPG • Max 20 MB
                </p>
              </div>
              {importFile && !isProcessingImport && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleProcessImport();
                  }}
                  className="btn-primary px-4 py-2 text-sm flex items-center gap-2 mt-2"
                  disabled={!canUseAIImport}
                >
                  {canUseAIImport ? (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Extract Checklist Items
                    </>
                  ) : (
                    'Upgrade Required'
                  )}
                </button>
              )}
              {isProcessingImport && (
                <div className="flex items-center gap-2 mt-2 text-primary-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">AI is analyzing your document...</span>
                </div>
              )}
            </div>
          </div>

          {!canUseAIImport && (
            <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-md text-sm text-orange-800">
              Upgrade to Standard or Pro to unlock AI checklist imports.
            </div>
          )}

          {importError && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {importError}
            </div>
          )}

          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={newItemText}
              onChange={(e) => setNewItemText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  addChecklistItem();
                }
              }}
              placeholder="Enter checklist item (press Enter to add)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button
              type="button"
              onClick={addChecklistItem}
              className="btn-primary px-4 py-2 flex items-center gap-2"
              disabled={!newItemText.trim()}
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>

          {wizardData.checklist.items.length > 0 ? (
            <div className="border border-gray-300 rounded-lg p-3 space-y-2 max-h-96 overflow-y-auto">
              {wizardData.checklist.items.map((item, index) => (
                <div key={index} className="bg-gray-50 rounded border border-gray-200">
                  {/* Item Header */}
                  <div className="flex items-center gap-2 p-2">
                    <button
                      type="button"
                      onClick={() => toggleItemExpanded(index)}
                      className="text-gray-600 hover:text-gray-800"
                      title={expandedItems.has(index) ? "Collapse evidence definitions" : "Expand to add evidence definitions"}
                    >
                      {expandedItems.has(index) ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                    <span className="text-sm font-medium text-gray-500 w-6">{index + 1}.</span>
                    <span className="flex-1 text-sm text-gray-700">{item.text}</span>
                    {item.evidenceDefinitions && item.evidenceDefinitions.length > 0 && (
                      <span className="text-xs text-gray-500 bg-blue-100 px-1.5 py-0.5 rounded">
                        {item.evidenceDefinitions.length} evidence{item.evidenceDefinitions.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => moveChecklistItem(index, 'up')}
                        disabled={index === 0}
                        className="text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed text-sm px-1"
                        title="Move up"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => moveChecklistItem(index, 'down')}
                        disabled={index === wizardData.checklist.items.length - 1}
                        className="text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed text-sm px-1"
                        title="Move down"
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        onClick={() => removeChecklistItem(index)}
                        className="text-red-600 hover:text-red-800 text-sm px-1"
                        title="Remove item"
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  {/* Evidence Definitions Section */}
                  {expandedItems.has(index) && (
                    <div className="pl-6 pr-2 pb-2 space-y-2 border-t border-gray-200 bg-white">
                      <div className="text-xs font-medium text-gray-700 pt-2">
                        Evidence Definitions (Optional)
                      </div>
                      
                      {/* Add New Evidence Definition */}
                      <div className="space-y-2 p-2 bg-gray-50 rounded border border-gray-200">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-0.5">
                              Evidence Name *
                            </label>
                            <input
                              type="text"
                              value={newEvidenceName[index] || ''}
                              onChange={(e) => setNewEvidenceName({ ...newEvidenceName, [index]: e.target.value })}
                              placeholder="e.g., Joint Intent, Flood Determination"
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                            />
                          </div>
                          <div className="flex items-end">
                            <label className="flex items-center gap-1 text-xs text-gray-700">
                              <input
                                type="checkbox"
                                checked={newEvidenceRequired[index] || false}
                                onChange={(e) => setNewEvidenceRequired({ ...newEvidenceRequired, [index]: e.target.checked })}
                                className="w-3 h-3"
                              />
                              Required
                            </label>
                          </div>
                        </div>


                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-0.5">
                            Description (Optional)
                          </label>
                          <input
                            type="text"
                            value={newEvidenceDescription[index] || ''}
                            onChange={(e) => setNewEvidenceDescription({ ...newEvidenceDescription, [index]: e.target.value })}
                            placeholder="Helper text for this evidence requirement"
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={() => addEvidenceDefinition(index)}
                          disabled={!newEvidenceName[index]?.trim()}
                          className="btn-primary px-2 py-1 text-xs w-full"
                        >
                          <Plus className="w-3 h-3 inline mr-1" />
                          Add Evidence Definition
                        </button>
                      </div>

                      {/* Existing Evidence Definitions */}
                      {item.evidenceDefinitions && item.evidenceDefinitions.length > 0 && (
                        <div className="space-y-2">
                          {item.evidenceDefinitions.map((evidenceDef, evidenceIndex) => {
                            const evidenceKey = getEvidenceDefKey(index, evidenceIndex);
                            const isExpanded = expandedEvidenceDefs.has(evidenceKey);
                            const fieldCount = evidenceDef.fieldDefinitions?.length || 0;
                            
                            return (
                              <div key={evidenceIndex} className="bg-white border border-gray-200 rounded text-xs">
                                {/* Evidence Definition Header */}
                                <div className="flex items-start gap-2 p-2">
                                  <button
                                    type="button"
                                    onClick={() => toggleEvidenceDefExpanded(index, evidenceIndex)}
                                    className="text-gray-600 hover:text-gray-800"
                                    title={isExpanded ? "Collapse fields" : "Expand to manage fields"}
                                  >
                                    {isExpanded ? (
                                      <ChevronDown className="w-4 h-4" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4" />
                                    )}
                                  </button>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-1 mb-1">
                                      <span className="font-medium text-gray-700">
                                        {evidenceDef.evidenceName || 'Unknown Evidence'}
                                      </span>
                                      {evidenceDef.required && (
                                        <span className="bg-red-100 text-red-700 px-1 rounded text-xs">Required</span>
                                      )}
                                      {!evidenceDef.required && (
                                        <span className="bg-gray-100 text-gray-600 px-1 rounded text-xs">Optional</span>
                                      )}
                                      {fieldCount > 0 && (
                                        <span className="bg-blue-100 text-blue-700 px-1 rounded text-xs">
                                          {fieldCount} field{fieldCount !== 1 ? 's' : ''}
                                        </span>
                                      )}
                                    </div>
                                    {evidenceDef.description && (
                                      <div className="text-gray-600 italic mt-1">
                                        {evidenceDef.description}
                                      </div>
                                    )}
                                    {evidenceDef.workflowRules && (
                                      <div className="text-blue-600 text-xs mt-1 bg-blue-50 p-1 rounded border border-blue-200">
                                        <strong>Workflow Rules:</strong> {evidenceDef.workflowRules}
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeEvidenceDefinition(index, evidenceIndex)}
                                    className="text-red-600 hover:text-red-800"
                                    title="Remove evidence definition"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>

                                {/* Field Definitions Section */}
                                {isExpanded && (
                                  <div className="pl-6 pr-2 pb-2 space-y-2 border-t border-gray-200 bg-gray-50">
                                    {/* Evidence Definition Edit Section */}
                                    <div className="p-2 bg-white border border-gray-200 rounded space-y-2 mb-2">
                                      <div className="text-xs font-medium text-gray-700">Evidence Definition</div>
                                      
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                          Description (Optional)
                                        </label>
                                        <input
                                          type="text"
                                          value={evidenceDef.description || ''}
                                          onChange={(e) => {
                                            const updatedItems = [...wizardData.checklist.items];
                                            if (!updatedItems[index] || !updatedItems[index].evidenceDefinitions) return;
                                            const evidenceDefs = [...updatedItems[index].evidenceDefinitions!];
                                            evidenceDefs[evidenceIndex] = {
                                              ...evidenceDefs[evidenceIndex],
                                              description: e.target.value.trim() || undefined,
                                            };
                                            updatedItems[index] = {
                                              ...updatedItems[index],
                                              evidenceDefinitions: evidenceDefs
                                            };
                                            setWizardData({
                                              ...wizardData,
                                              checklist: {
                                                ...wizardData.checklist,
                                                items: updatedItems
                                              }
                                            });
                                          }}
                                          placeholder="Helper text for this evidence requirement"
                                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                        />
                                      </div>

                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                          Workflow Rules (Optional)
                                        </label>
                                        <textarea
                                          value={evidenceDef.workflowRules || ''}
                                          onChange={(e) => {
                                            const updatedItems = [...wizardData.checklist.items];
                                            if (!updatedItems[index] || !updatedItems[index].evidenceDefinitions) return;
                                            const evidenceDefs = [...updatedItems[index].evidenceDefinitions!];
                                            evidenceDefs[evidenceIndex] = {
                                              ...evidenceDefs[evidenceIndex],
                                              workflowRules: e.target.value.trim() || undefined,
                                            };
                                            updatedItems[index] = {
                                              ...updatedItems[index],
                                              evidenceDefinitions: evidenceDefs
                                            };
                                            setWizardData({
                                              ...wizardData,
                                              checklist: {
                                                ...wizardData.checklist,
                                                items: updatedItems
                                              }
                                            });
                                          }}
                                          placeholder="e.g., This evidence is required if property is in SFHA"
                                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                          rows={3}
                                        />
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          💡 Describe when this evidence is required or optional
                                        </p>
                                      </div>
                                    </div>

                                    <div className="text-xs font-medium text-gray-700 pt-2">
                                      Field Definitions (Optional)
                                    </div>

                                    {/* Existing Fields */}
                                    {evidenceDef.fieldDefinitions && evidenceDef.fieldDefinitions.length > 0 && (
                                      <div className="space-y-1.5">
                                        {evidenceDef.fieldDefinitions.map((field, fieldIndex) => (
                                          <div key={fieldIndex} className="p-2 bg-white border border-gray-200 rounded text-xs">
                                            <div className="flex items-center justify-between mb-1">
                                              <div className="flex items-center gap-1 flex-wrap">
                                                <span className="font-medium text-gray-700">{field.label}</span>
                                                <span className="text-gray-500">({field.key})</span>
                                                <span className="text-gray-500 text-xs">- {field.type}</span>
                                                {field.required && (
                                                  <span className="bg-red-100 text-red-700 px-1 rounded text-xs">Required</span>
                                                )}
                                                {(() => {
                                                  const fileNameKeys = field.fileNameKeys && field.fileNameKeys.length > 0 
                                                    ? field.fileNameKeys 
                                                    : (field.fileNameKey ? [field.fileNameKey] : []);
                                                  return fileNameKeys.length > 0 && (
                                                    <span className="bg-blue-100 text-blue-700 px-1 rounded text-xs" title={`Only processes files with these keys in filename: ${fileNameKeys.join(', ')}`}>
                                                      File: {fileNameKeys.join(', ')}
                                                    </span>
                                                  );
                                                })()}
                                              </div>
                                              <button
                                                type="button"
                                                onClick={() => removeFieldDefinition(index, evidenceIndex, fieldIndex)}
                                                className="text-red-600 hover:text-red-800"
                                                title="Remove field"
                                              >
                                                <Trash2 className="w-3 h-3" />
                                              </button>
                                            </div>
                                            {field.enumValues && field.enumValues.length > 0 && (
                                              <div className="text-gray-600 text-xs mt-1">
                                                Values: {field.enumValues.join(', ')}
                                              </div>
                                            )}
                                            {field.workflowRules && (
                                              <div className="text-blue-600 text-xs mt-1 bg-blue-50 p-1 rounded border border-blue-200">
                                                <strong>Workflow Rules:</strong> {field.workflowRules}
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Add New Field - Same UI as ChecklistForm */}
                                    <div className="p-2 bg-white border border-gray-200 rounded space-y-2">
                                      <div className="text-xs font-medium text-gray-700">Add Field</div>
                                      
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                            Key *
                                          </label>
                                          <input
                                            type="text"
                                            value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.key || ''}
                                            onChange={(e) => setNewFieldData({
                                              ...newFieldData,
                                              [getFieldKey(index, evidenceIndex, 'new')]: {
                                                ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                key: e.target.value
                                              }
                                            })}
                                            placeholder="e.g., recordId, evidenceDate"
                                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                          />
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                            Label *
                                          </label>
                                          <input
                                            type="text"
                                            value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.label || ''}
                                            onChange={(e) => setNewFieldData({
                                              ...newFieldData,
                                              [getFieldKey(index, evidenceIndex, 'new')]: {
                                                ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                label: e.target.value
                                              }
                                            })}
                                            placeholder="e.g., AFR Certificate ID"
                                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                          />
                                        </div>
                                      </div>

                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                            Type *
                                          </label>
                                          <select
                                            value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.type || ''}
                                            onChange={(e) => {
                                              const type = e.target.value as EvidenceFieldDefinition['type'];
                                              setNewFieldData({
                                                ...newFieldData,
                                                [getFieldKey(index, evidenceIndex, 'new')]: {
                                                  ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                  type: type,
                                                  enumValues: type === 'enum' ? [] : undefined
                                                }
                                              });
                                            }}
                                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                          >
                                            <option value="">Select type...</option>
                                            <option value="string">String</option>
                                            <option value="date">Date</option>
                                            <option value="number">Number</option>
                                            <option value="boolean">Boolean</option>
                                            <option value="enum">Enum</option>
                                          </select>
                                        </div>
                                        <div className="flex items-end">
                                          <label className="flex items-center gap-1 text-xs text-gray-700">
                                            <input
                                              type="checkbox"
                                              checked={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.required || false}
                                              onChange={(e) => setNewFieldData({
                                                ...newFieldData,
                                                [getFieldKey(index, evidenceIndex, 'new')]: {
                                                  ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                  required: e.target.checked
                                                }
                                              })}
                                              className="w-3 h-3"
                                            />
                                            Required
                                          </label>
                                        </div>
                                      </div>

                                      {/* File Name Key */}
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                          File Name Key(s) (Optional)
                                        </label>
                                        <input
                                          type="text"
                                          value={(() => {
                                            const fieldData = newFieldData[getFieldKey(index, evidenceIndex, 'new')];
                                            if (fieldData?.fileNameKeys && fieldData.fileNameKeys.length > 0) {
                                              return fieldData.fileNameKeys.join(', ');
                                            }
                                            return fieldData?.fileNameKey || '';
                                          })()}
                                          onChange={(e) => {
                                            const value = e.target.value.trim();
                                            const keys = value ? value.split(',').map(k => k.trim()).filter(k => k.length > 0) : [];
                                            
                                            setNewFieldData({
                                              ...newFieldData,
                                              [getFieldKey(index, evidenceIndex, 'new')]: {
                                                ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                fileNameKeys: keys.length > 1 ? keys : undefined,
                                                fileNameKey: keys.length === 1 ? keys[0] : (keys.length === 0 ? undefined : keys[0])
                                              }
                                            });
                                          }}
                                          placeholder="e.g., loan-number, application-number (comma-separated for multiple)"
                                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                        />
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          Enter one or more keys separated by commas. Files matching any key will be processed.
                                        </p>
                                      </div>

                                      {/* Workflow Rules */}
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                          Workflow Rules (Optional)
                                        </label>
                                        <textarea
                                          value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.workflowRules || ''}
                                          onChange={(e) => setNewFieldData({
                                            ...newFieldData,
                                            [getFieldKey(index, evidenceIndex, 'new')]: {
                                              ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                              workflowRules: e.target.value.trim() || undefined
                                            }
                                          })}
                                          placeholder="e.g., This field is required if deliveryMethod is EMAIL"
                                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                          rows={2}
                                        />
                                        <p className="text-xs text-gray-500 mt-0.5">
                                          💡 Describe when this field is required or optional based on other field values
                                        </p>
                                      </div>

                                      {/* Enum Values */}
                                      {newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.type === 'enum' && (
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                            Enum Values (comma-separated)
                                          </label>
                                          <input
                                            type="text"
                                            value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.enumValues?.join(', ') || ''}
                                            onChange={(e) => setNewFieldData({
                                              ...newFieldData,
                                              [getFieldKey(index, evidenceIndex, 'new')]: {
                                                ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                enumValues: e.target.value.split(',').map(v => v.trim()).filter(v => v)
                                              }
                                            })}
                                            placeholder="Value1, Value2, Value3"
                                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                          />
                                        </div>
                                      )}

                                      {/* Extraction Hints */}
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                          Extraction Hints (Optional)
                                        </label>
                                        <div className="space-y-1">
                                          <input
                                            type="text"
                                            value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.extractionHints?.keywords?.join(', ') || ''}
                                            onChange={(e) => setNewFieldData({
                                              ...newFieldData,
                                              [getFieldKey(index, evidenceIndex, 'new')]: {
                                                ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                extractionHints: {
                                                  ...newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.extractionHints,
                                                  keywords: e.target.value.split(',').map(v => v.trim()).filter(v => v)
                                                }
                                              }
                                            })}
                                            placeholder="Keywords (comma-separated)"
                                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                          />
                                          <input
                                            type="text"
                                            value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.extractionHints?.anchors?.join(', ') || ''}
                                            onChange={(e) => setNewFieldData({
                                              ...newFieldData,
                                              [getFieldKey(index, evidenceIndex, 'new')]: {
                                                ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                extractionHints: {
                                                  ...newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.extractionHints,
                                                  anchors: e.target.value.split(',').map(v => v.trim()).filter(v => v)
                                                }
                                              }
                                            })}
                                            placeholder="Anchor text (comma-separated)"
                                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                          />
                                          <input
                                            type="text"
                                            value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.extractionHints?.regex || ''}
                                            onChange={(e) => setNewFieldData({
                                              ...newFieldData,
                                              [getFieldKey(index, evidenceIndex, 'new')]: {
                                                ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                extractionHints: {
                                                  ...newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.extractionHints,
                                                  regex: e.target.value || undefined
                                                }
                                              }
                                            })}
                                            placeholder="Regex pattern (optional)"
                                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                          />
                                        </div>
                                      </div>

                                      {/* Normalization Rules */}
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                          Normalization (Optional)
                                        </label>
                                        <div className="grid grid-cols-2 gap-2">
                                          {newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.type === 'date' && (
                                            <input
                                              type="text"
                                              value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.normalizationRules?.dateFormat || ''}
                                              onChange={(e) => setNewFieldData({
                                                ...newFieldData,
                                                [getFieldKey(index, evidenceIndex, 'new')]: {
                                                  ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                  normalizationRules: {
                                                    ...newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.normalizationRules,
                                                    dateFormat: e.target.value || undefined
                                                  }
                                                }
                                              })}
                                              placeholder="Date format (e.g., YYYY-MM-DD)"
                                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                            />
                                          )}
                                          <label className="flex items-center gap-1 text-xs text-gray-700">
                                            <input
                                              type="checkbox"
                                              checked={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.normalizationRules?.trim || false}
                                              onChange={(e) => setNewFieldData({
                                                ...newFieldData,
                                                [getFieldKey(index, evidenceIndex, 'new')]: {
                                                  ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                  normalizationRules: {
                                                    ...newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.normalizationRules,
                                                    trim: e.target.checked || undefined
                                                  }
                                                }
                                              })}
                                              className="w-3 h-3"
                                            />
                                            Trim whitespace
                                          </label>
                                        </div>
                                      </div>

                                      {/* Validation Rules */}
                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-0.5">
                                          Validation Rules (Optional)
                                        </label>
                                        <div className="grid grid-cols-2 gap-2">
                                          {newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.type === 'number' && (
                                            <>
                                              <input
                                                type="number"
                                                value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.validationRules?.min || ''}
                                                onChange={(e) => setNewFieldData({
                                                  ...newFieldData,
                                                  [getFieldKey(index, evidenceIndex, 'new')]: {
                                                    ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                    validationRules: {
                                                      ...newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.validationRules,
                                                      min: e.target.value ? Number(e.target.value) : undefined
                                                    }
                                                  }
                                                })}
                                                placeholder="Min value"
                                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                              />
                                              <input
                                                type="number"
                                                value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.validationRules?.max || ''}
                                                onChange={(e) => setNewFieldData({
                                                  ...newFieldData,
                                                  [getFieldKey(index, evidenceIndex, 'new')]: {
                                                    ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                    validationRules: {
                                                      ...newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.validationRules,
                                                      max: e.target.value ? Number(e.target.value) : undefined
                                                    }
                                                  }
                                                })}
                                                placeholder="Max value"
                                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                              />
                                            </>
                                          )}
                                          {newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.type === 'string' && (
                                            <>
                                              <input
                                                type="number"
                                                value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.validationRules?.minLength || ''}
                                                onChange={(e) => setNewFieldData({
                                                  ...newFieldData,
                                                  [getFieldKey(index, evidenceIndex, 'new')]: {
                                                    ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                    validationRules: {
                                                      ...newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.validationRules,
                                                      minLength: e.target.value ? Number(e.target.value) : undefined
                                                    }
                                                  }
                                                })}
                                                placeholder="Min length"
                                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                              />
                                              <input
                                                type="number"
                                                value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.validationRules?.maxLength || ''}
                                                onChange={(e) => setNewFieldData({
                                                  ...newFieldData,
                                                  [getFieldKey(index, evidenceIndex, 'new')]: {
                                                    ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                    validationRules: {
                                                      ...newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.validationRules,
                                                      maxLength: e.target.value ? Number(e.target.value) : undefined
                                                    }
                                                  }
                                                })}
                                                placeholder="Max length"
                                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                              />
                                            </>
                                          )}
                                          <input
                                            type="text"
                                            value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.validationRules?.mustBeLessThan || ''}
                                            onChange={(e) => setNewFieldData({
                                              ...newFieldData,
                                              [getFieldKey(index, evidenceIndex, 'new')]: {
                                                ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                validationRules: {
                                                  ...newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.validationRules,
                                                  mustBeLessThan: e.target.value || undefined
                                                }
                                              }
                                            })}
                                            placeholder="Must be < field key"
                                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                          />
                                          <input
                                            type="text"
                                            value={newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.validationRules?.mustBeGreaterThan || ''}
                                            onChange={(e) => setNewFieldData({
                                              ...newFieldData,
                                              [getFieldKey(index, evidenceIndex, 'new')]: {
                                                ...newFieldData[getFieldKey(index, evidenceIndex, 'new')],
                                                validationRules: {
                                                  ...newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.validationRules,
                                                  mustBeGreaterThan: e.target.value || undefined
                                                }
                                              }
                                            })}
                                            placeholder="Must be > field key"
                                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                                          />
                                        </div>
                                      </div>

                                      <button
                                        type="button"
                                        onClick={() => addFieldDefinition(index, evidenceIndex)}
                                        disabled={!newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.key?.trim() || 
                                                 !newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.label?.trim() ||
                                                 !newFieldData[getFieldKey(index, evidenceIndex, 'new')]?.type}
                                        className="btn-primary px-2 py-1 text-xs w-full"
                                      >
                                        <Plus className="w-3 h-3 inline mr-1" />
                                        Add Field
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <div className="text-xs text-gray-500 text-center pt-2 border-t border-gray-200">
                {wizardData.checklist.items.length} item{wizardData.checklist.items.length !== 1 ? 's' : ''} total
              </div>
            </div>
          ) : (
            <div className="border border-gray-300 rounded-lg p-4 text-center text-sm text-gray-500">
              No items yet. Add items manually above or upload a document to extract items with AI.
            </div>
          )}
        </div>

        {/* Location Restriction */}
        <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
          <div className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              id="location-specific"
              checked={wizardData.isLocationSpecific}
              onChange={(e) => handleLocationSpecificToggle(e.target.checked)}
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label htmlFor="location-specific" className="text-sm font-medium text-gray-700">
              Available only to specific locations
            </label>
          </div>
          {wizardData.isLocationSpecific && (
            <div className="mt-3 space-y-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={locationSearchTerm}
                  onChange={(e) => setLocationSearchTerm(e.target.value)}
                  placeholder="Search locations..."
                  className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md"
                />
              </div>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2 bg-white">
                {filteredLocations.length === 0 ? (
                  <div className="text-sm text-gray-500 text-center py-2">
                    {locationSearchTerm ? 'No locations found' : 'No locations available'}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {filteredLocations.map((location) => (
                      <label
                        key={location.id}
                        className="flex items-center gap-2 p-1.5 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={(wizardData.locationIds || []).includes(location.id || '')}
                          onChange={() => location.id && handleLocationToggle(location.id)}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">{location.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {(wizardData.locationIds || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {locations
                    .filter(loc => (wizardData.locationIds || []).includes(loc.id || ''))
                    .map((location) => (
                      <span
                        key={location.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-800 rounded text-sm"
                      >
                        {location.name}
                        <button
                          type="button"
                          onClick={() => location.id && handleLocationToggle(location.id)}
                          className="hover:text-primary-900"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTemplateStep = () => {
    // If creating template, return null (ComplianceForm rendered separately)
    if (isCreatingTemplate) {
      return null;
    }

    // Filter templates based on search term
    const filteredTemplates = templates.filter(template => {
      if (!templateSearchTerm) return true;
      const search = templateSearchTerm.toLowerCase();
      return (
        template.name.toLowerCase().includes(search) ||
        template.category?.toLowerCase().includes(search)
      );
    });

    return (
      <div className="space-y-6 p-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Link Compliance</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
            <p className="text-sm text-blue-800">
              <strong>Why this is important:</strong> Instead of just creating a checklist and leaving it unused, 
              linking a compliance template ensures your company has a policy in place to periodically review 
              checklist completion. This helps maintain accountability, track progress over time, and ensures 
              compliance requirements are consistently met.
            </p>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Compliance Template <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setIsCreatingTemplate(true)}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add Compliance
            </button>
          </div>

          {/* Search Bar */}
          <div className="mb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={templateSearchTerm}
                onChange={(e) => setTemplateSearchTerm(e.target.value)}
                placeholder="Search compliance templates..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {isLoadingTemplates ? (
            <div className="border border-gray-300 rounded-lg p-4 text-center text-sm text-gray-500">
              Loading templates...
            </div>
          ) : templates.length === 0 ? (
            <div className="border border-gray-300 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-600 mb-3">No templates found. Create one to continue.</p>
              <button
                type="button"
                onClick={() => setIsCreatingTemplate(true)}
                className="btn-primary px-4 py-2 text-sm"
              >
                Create Your First Template
              </button>
            </div>
          ) : filteredTemplates.length === 0 ? (
            <div className="border border-gray-300 rounded-lg p-4 text-center text-sm text-gray-500">
              No templates match your search.
            </div>
          ) : (
            <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
              {filteredTemplates.map((template) => {
                    const isSelected = wizardData.templateId === template.id;
                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => template.id && handleTemplateSelect(template.id)}
                        className={`w-full text-left p-4 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-primary-50 border-primary-200' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {isSelected && <CheckCircle2 className="w-5 h-5 text-primary-600 flex-shrink-0" />}
                              <h4 className={`font-medium ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                                {template.name}
                              </h4>
                            </div>
                            {template.category && (
                              <p className="text-xs text-gray-500 mt-1">Category: {template.category}</p>
                            )}
                            {template.frequency && (
                              <p className="text-xs text-gray-500">Frequency: {template.frequency}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
          )}

          {wizardData.template && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-900">Compliance Selected</span>
              </div>
              <p className="text-sm text-green-700">{wizardData.template.name}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const handleAssetTypeSelect = (assetTypeId: string) => {
    const assetType = assetTypes.find(a => a.id === assetTypeId);
    setWizardData({
      ...wizardData,
      assetTypeId,
      isCreatingAssetType: false,
      assetType: null
    });
  };

  const renderAssetTypeStep = () => {
    // Filter asset types based on search term
    const filteredAssetTypes = assetTypes.filter(assetType => {
      if (!assetTypeSearchTerm) return true;
      const search = assetTypeSearchTerm.toLowerCase();
      return (
        assetType.name.toLowerCase().includes(search) ||
        assetType.description?.toLowerCase().includes(search)
      );
    });

    return (
      <div className="space-y-6 p-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Asset Type <span className="text-red-500">*</span></h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
            <p className="text-sm text-blue-800">
              <strong>Why this is important:</strong> Linking a checklist to an asset type ensures that when you create assets 
              of that type, the checklist will automatically be associated with them. Instead of manually assigning checklists 
              to each asset, asset types help organize and automate the process.
            </p>
            <p className="text-sm text-blue-800 mt-2">
              <strong>Example:</strong> Create a "Room" asset type and add all your room numbers as assets (Room 101, Room 102, etc.). 
              The checklist will be automatically available to be completed for all room numbers, making it easy to track compliance 
              across your entire facility.
            </p>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Asset Type <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => setWizardData({
                ...wizardData,
                isCreatingAssetType: true,
                assetTypeId: null,
                assetType: {
                  name: wizardData.checklist.name.replace('Checklist', '').trim() || '',
                  description: wizardData.checklist.description || ''
                }
              })}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add New Asset Type
            </button>
          </div>

          {!wizardData.isCreatingAssetType ? (
            <>
              {/* Search Bar */}
              <div className="mb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={assetTypeSearchTerm}
                    onChange={(e) => setAssetTypeSearchTerm(e.target.value)}
                    placeholder="Search asset types..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {isLoadingAssetTypes ? (
                <div className="border border-gray-300 rounded-lg p-4 text-center text-sm text-gray-500">
                  Loading asset types...
                </div>
              ) : assetTypes.length === 0 ? (
                <div className="border border-gray-300 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-600 mb-3">No asset types found. Create one to continue.</p>
                  <button
                    type="button"
                    onClick={() => setWizardData({
                      ...wizardData,
                      isCreatingAssetType: true,
                      assetTypeId: null,
                      assetType: {
                        name: wizardData.checklist.name.replace('Checklist', '').trim() || '',
                        description: wizardData.checklist.description || ''
                      }
                    })}
                    className="btn-primary px-4 py-2 text-sm"
                  >
                    Create Your First Asset Type
                  </button>
                </div>
              ) : filteredAssetTypes.length === 0 ? (
                <div className="border border-gray-300 rounded-lg p-4 text-center text-sm text-gray-500">
                  No asset types match your search.
                </div>
              ) : (
                <div className="border border-gray-300 rounded-lg max-h-96 overflow-y-auto">
                  {filteredAssetTypes.map((assetType) => {
                    const isSelected = wizardData.assetTypeId === assetType.id;
                    return (
                      <button
                        key={assetType.id}
                        type="button"
                        onClick={() => assetType.id && handleAssetTypeSelect(assetType.id)}
                        className={`w-full text-left p-4 border-b border-gray-200 last:border-b-0 hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-primary-50 border-primary-200' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {isSelected && <CheckCircle2 className="w-5 h-5 text-primary-600 flex-shrink-0" />}
                              <h4 className={`font-medium ${isSelected ? 'text-primary-900' : 'text-gray-900'}`}>
                                {assetType.name}
                              </h4>
                            </div>
                            {assetType.description && (
                              <p className="text-xs text-gray-500 mt-1">{assetType.description}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {wizardData.assetTypeId && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-900">Asset Type Selected</span>
                  </div>
                  <p className="text-sm text-green-700">
                    {assetTypes.find(a => a.id === wizardData.assetTypeId)?.name}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Asset Type Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={wizardData.assetType?.name || ''}
                  onChange={(e) => setWizardData({
                    ...wizardData,
                    assetType: {
                      ...(wizardData.assetType || { name: '', description: '' }),
                      name: e.target.value
                    }
                  })}
                  placeholder="e.g., Fire Extinguishers"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">This will be auto-linked to the compliance template you selected.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={wizardData.assetType?.description || ''}
                  onChange={(e) => setWizardData({
                    ...wizardData,
                    assetType: {
                      ...(wizardData.assetType || { name: '', description: '' }),
                      description: e.target.value
                    }
                  })}
                  placeholder="Describe this asset type..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <button
                type="button"
                onClick={() => setWizardData({
                  ...wizardData,
                  isCreatingAssetType: false,
                  assetType: null
                })}
                className="text-sm text-gray-600 hover:text-gray-700 font-medium"
              >
                ← Back to select existing asset type
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* ComplianceForm as right panel when creating template */}
      {isCreatingTemplate && currentStep === 'template' && (
        <ComplianceForm
          template={undefined}
          onSubmit={handleTemplateCreated}
          onCancel={() => setIsCreatingTemplate(false)}
          categories={categories.length > 0 ? categories : ['Fire Safety', 'Life Safety', 'Health & Safety', 'Equipment Maintenance', 'Building Maintenance', 'Environmental', 'Security', 'Other']}
          zIndex={210}
        />
      )}
      
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[200] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Quick Setup: Create Checklist</h2>
            <p className="text-sm text-gray-600 mt-1">Follow the steps below to create your checklist with all related items</p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 p-1"
            title="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Indicator */}
        {renderProgressIndicator()}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {currentStep === 'checklist' && renderChecklistStep()}
          {currentStep === 'template' && renderTemplateStep()}
          {currentStep === 'asset-type' && renderAssetTypeStep()}
        </div>

        {/* Footer - Only show when not creating template */}
        {!isCreatingTemplate && (
          <div className="border-t border-gray-200 bg-gray-50 p-6 flex justify-between gap-3">
            <button
              type="button"
              onClick={isFirstStep ? onCancel : handleBack}
              className="btn-secondary px-6 py-2 flex items-center gap-2"
              disabled={isSaving}
            >
              <ChevronLeft className="w-4 h-4" />
              {isFirstStep ? 'Cancel' : 'Back'}
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="btn-primary px-6 py-2 flex items-center gap-2"
              disabled={isSaving}
            >
              {isSaving ? (
                <>Saving...</>
              ) : isLastStep ? (
                <>
                  Complete Setup
                  <CheckCircle2 className="w-4 h-4" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default ChecklistQuickSetupWizard;

